import { Box, Button, Typography, webPalette, webFonts } from '@munifocus/ui';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteLayout, fadeUp } from '../components/SiteLayout';
import { PageMeta } from '../components/PageMeta';
import { Wrap } from '../components/Wrap';
import { Kicker } from '../components/Kicker';
import { SectionHead } from '../components/SectionHead';
import { ContactTrigger, useContactDialog } from '../components/ContactDialog';
import { formatBulletinDate, formatTag, useBulletins, useCategoryLabel } from '../content/bulletin';
import { useTrainingTopics } from '../content/training';

// The hero goes two-column (and shows the product render) here. Sits between
// MUI's md/lg so it covers the bulk of laptops/desktops; below it the render
// is hidden rather than stacked.
const HERO_TWO_COL = '@media (min-width:1000px)';

interface TopicItem {
  name: string;
  blurb: string;
}

export function Home() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as 'en' | 'fr';
  const categoryLabel = useCategoryLabel();
  const { open } = useContactDialog();
  // Surface the 6 most recently created topics from the admin-managed catalogue;
  // fall back to the bundled i18n copy if the API is unreachable or empty.
  const fallbackTopics = t('web.home.training.topics', { returnObjects: true }) as TopicItem[];
  const { data: apiTopics } = useTrainingTopics();
  const topics: TopicItem[] =
    apiTopics && apiTopics.length > 0
      ? [...apiTopics]
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .slice(0, 6)
          .map((tp) => ({ name: tp.title, blurb: tp.blurb }))
      : fallbackTopics;
  const appBullets = t('web.home.app.bullets', { returnObjects: true }) as string[];
  const { data: issues = [] } = useBulletins();
  const [featured, ...recent] = issues.slice(0, 5);

  return (
    <SiteLayout>
      <PageMeta titleKey="web.meta.home" descriptionKey="web.meta.homeDescription" />
      {/* HERO */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: { xs: 9, md: 11 },
          borderBottom: `1px solid ${webPalette.line}`,
        }}
      >
        <Wrap
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 8,
            alignItems: 'center',
            // Two columns from ~1000px up; below that the render is hidden and
            // this collapses to a single-column text hero. Tight gap keeps the
            // render close to the copy.
            [HERO_TWO_COL]: { gridTemplateColumns: '1.15fr 0.85fr', gap: 2 },
          }}
        >
          {/* LEFT — headline, lead, CTAs */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: webFonts.serif,
                fontWeight: 500,
                // Scales with width (down to the narrower two-column layout near
                // 1000px) and caps at 62px, so "local government." stays on one
                // line and never shatters the headline.
                fontSize: 'clamp(2.25rem, 5vw, 62px)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
                // Wide enough to keep "local government." on one line (17 chars)
                // so the accent never orphans "local" on its own row.
                maxWidth: '18ch',
                overflowWrap: 'break-word',
                ...fadeUp(0),
              }}
            >
              {t('web.home.h1Pre')}
              <Box component="span" sx={{ color: webPalette.tealInk, fontStyle: 'italic' }}>
                {t('web.home.h1Accent')}
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: 20,
                lineHeight: 1.55,
                color: webPalette.muted,
                maxWidth: '54ch',
                mt: { xs: 4, md: 4.5 },
                ...fadeUp(0.12),
              }}
            >
              {t('web.home.lead')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 4.5, flexWrap: 'wrap', ...fadeUp(0.2) }}>
              <Button variant="contained" component={RouterLink} to="/bulletin">
                {t('web.home.ctaBulletin')}
              </Button>
              <Button variant="outlined" component={RouterLink} to="/training">
                {t('web.home.ctaTraining')}
              </Button>
            </Box>
          </Box>

          {/* RIGHT — product showcase (carries the "We speak municipal" line on-screen) */}
          <Box
            sx={{
              position: 'relative',
              minWidth: 0,
              // Only show the showcase in the two-column layout; below ~1000px
              // it would stack to a second row, which reads poorly.
              display: 'none',
              alignItems: 'center',
              justifyContent: 'flex-end',
              ...fadeUp(0.22),
              [HERO_TWO_COL]: { display: 'flex' },
            }}
          >
            {/* Soft brand glow so the render is grounded, not floating on white */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: '-14% -8% -14% -2%',
                borderRadius: '50%',
                background: `radial-gradient(closest-side, ${webPalette.overlayTealFocusRing}, transparent 72%)`,
                pointerEvents: 'none',
                // The gradient's `transparent` stop is rgba(0,0,0,0); when a page
                // is flattened to PDF the alpha is dropped and it renders as a
                // solid black disc behind the (transparent) hero render. It's a
                // purely decorative glow, so drop it from print entirely.
                '@media print': { display: 'none' },
              }}
            />
            <Box
              component="img"
              // The showcase render carries the on-screen wordmark ("We speak
              // municipal" / "Nous parlons municipal"), so it's localized.
              src={
                lang === 'fr' ? '/images/home-hero-devices-fr.png' : '/images/home-hero-devices.png'
              }
              alt={t('web.home.kicker')}
              loading="eager"
              fetchPriority="high"
              sx={{
                position: 'relative',
                height: 'auto',
                display: 'block',
                // Fills its column (left edge stays just past the copy,
                // separated only by the small grid gap) with a right bleed that
                // grows in one step on wider desktops — modest near 1000 (so the
                // render isn't over-clipped) and larger from 1280 up where the
                // content fills the screen, so it reads as bleeding off the
                // right edge. Both are fixed px, anchored to the centered
                // (max-1180) layout — no runaway drift on large monitors.
                //
                // The EN and FR renders share identical dimensions (1448×1086),
                // so the box is the same in both languages — switching only
                // swaps the on-screen wordmark, the render never resizes.
                width: '120%',
                // Pin the render box to the images' intrinsic ratio (1448×1086)
                // so the box fits either variant with no letterbox. Reserving
                // the ratio also prevents the layout from shifting while the
                // image loads (no CLS).
                aspectRatio: '1448 / 1086',
                objectFit: 'contain',
                mr: '-56px',
                '@media (min-width:1280px)': { width: '130%', mr: '-120px' },
              }}
            />
          </Box>
        </Wrap>
      </Box>

      {/* BULLETIN */}
      <Box
        component="section"
        sx={{
          py: 11,
          backgroundColor: webPalette.white,
          borderTop: `1px solid ${webPalette.line}`,
          borderBottom: `1px solid ${webPalette.line}`,
        }}
      >
        <Wrap>
          <SectionHead
            kicker={t('web.home.bull.kicker')}
            title={t('web.home.bull.title')}
            aside={<TextLink to="/bulletin">{t('web.home.bull.all')}</TextLink>}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' },
              gap: 4.5,
            }}
          >
            {featured ? (
              <Box
                component={RouterLink}
                to={`/bulletin/${featured.slug}`}
                sx={{
                  borderRight: { md: `1px solid ${webPalette.line}` },
                  pr: { md: 4.5 },
                  pb: { xs: 4, md: 0 },
                  borderBottom: { xs: `1px solid ${webPalette.line}`, md: 'none' },
                  color: 'inherit',
                  textDecoration: 'none',
                  display: 'block',
                }}
              >
                <Typography sx={{ fontSize: 13, color: webPalette.muted }}>
                  {formatBulletinDate(featured.date, lang)}
                </Typography>
                <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap', my: 1.625 }}>
                  <Tag>{categoryLabel(featured.category)}</Tag>
                  {featured.keywords.slice(0, 2).map((kw) => (
                    <Tag key={kw}>{formatTag(kw)}</Tag>
                  ))}
                </Box>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: webFonts.serif,
                    fontWeight: 500,
                    fontSize: 30,
                    lineHeight: 1.18,
                    mb: 1.25,
                  }}
                >
                  {featured.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    color: webPalette.muted,
                    lineHeight: 1.55,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {featured.excerpt}
                </Typography>
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {recent.slice(0, 2).map((it) => (
                <SmallIssue
                  key={it.slug}
                  slug={it.slug}
                  date={formatBulletinDate(it.date, lang)}
                  tags={[categoryLabel(it.category)]}
                  title={it.title}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {recent.slice(2, 4).map((it) => (
                <SmallIssue
                  key={it.slug}
                  slug={it.slug}
                  date={formatBulletinDate(it.date, lang)}
                  tags={[categoryLabel(it.category)]}
                  title={it.title}
                />
              ))}
            </Box>
          </Box>
        </Wrap>
      </Box>

      {/* TRAINING */}
      <Box component="section" sx={{ py: 11 }}>
        <Wrap>
          <SectionHead
            kicker={t('web.home.training.kicker')}
            title={t('web.home.training.title')}
            aside={<TextLink to="/training">{t('web.home.training.all')}</TextLink>}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: '1px',
              backgroundColor: webPalette.line,
              border: `1px solid ${webPalette.line}`,
            }}
          >
            {topics.map((tt, i) => (
              <Box
                key={i}
                component={RouterLink}
                to="/training"
                sx={{
                  backgroundColor: webPalette.cream,
                  p: '30px 28px',
                  transition: 'background-color 0.2s',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: webPalette.white },
                }}
              >
                <Box sx={{ fontFamily: webFonts.serif, fontSize: 15, color: webPalette.tealInk }}>
                  {String(i + 1).padStart(2, '0')}
                </Box>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: webFonts.serif,
                    fontWeight: 500,
                    fontSize: 23,
                    mt: 1.75,
                    mb: 1,
                  }}
                >
                  {tt.name}
                </Typography>
                <Typography sx={{ fontSize: 14.5, color: webPalette.muted, lineHeight: 1.5 }}>
                  {tt.blurb}
                </Typography>
              </Box>
            ))}
          </Box>
        </Wrap>
      </Box>

      {/* APP */}
      <Box
        component="section"
        sx={{
          backgroundColor: webPalette.greenDeep,
          color: webPalette.onDark,
          py: 12,
          overflow: 'hidden',
        }}
      >
        <Wrap
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr .95fr' },
            gap: 8,
            alignItems: 'center',
          }}
        >
          <Box>
            <Kicker color={webPalette.teal}>{t('web.home.app.kicker')}</Kicker>
            <Typography
              component="h2"
              sx={{
                fontFamily: webFonts.serif,
                fontWeight: 500,
                fontSize: { xs: 36, md: 48 },
                lineHeight: 1.06,
                letterSpacing: '-0.01em',
                my: 2.25,
              }}
            >
              {t('web.home.app.h2Pre')}{' '}
              <Box component="em" sx={{ color: webPalette.teal, fontStyle: 'italic' }}>
                {t('web.home.app.h2Em')}
              </Box>
            </Typography>
            <Typography
              sx={{
                color: webPalette.onDarkSubtle,
                fontSize: 18,
                lineHeight: 1.6,
                maxWidth: '46ch',
              }}
            >
              {t('web.home.app.lead')}
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: '30px 0 36px',
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.875,
              }}
            >
              {appBullets.map((b, i) => (
                <Box
                  key={i}
                  component="li"
                  sx={{
                    display: 'flex',
                    gap: 1.75,
                    alignItems: 'flex-start',
                    fontSize: 15.5,
                    color: webPalette.onDarkStrong,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: webPalette.teal,
                      mt: 1,
                      flex: 'none',
                    }}
                  />
                  <span>{b}</span>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => open()}
              sx={{
                backgroundColor: webPalette.teal,
                color: webPalette.greenDeep,
                '&:hover': { backgroundColor: webPalette.tealHover },
              }}
            >
              {t('web.home.app.cta')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src="/images/gov-phone-v4.png"
              alt={t('web.home.app.mockup')}
              sx={{
                width: { xs: 240, md: 268 },
                height: 'auto',
                display: 'block',
                filter: `drop-shadow(0 40px 80px ${webPalette.overlayBlackShadow})`,
              }}
            />
          </Box>
        </Wrap>
      </Box>

      {/* HELP */}
      <Box component="section" sx={{ backgroundColor: webPalette.parch, py: 11 }}>
        <Wrap
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 7.5 },
            alignItems: 'center',
          }}
        >
          <Box>
            <Kicker>{t('web.home.help.kicker')}</Kicker>
            <Typography
              component="h2"
              sx={{
                fontFamily: webFonts.serif,
                fontWeight: 500,
                fontSize: { xs: 32, md: 46 },
                lineHeight: 1.05,
                my: 2,
                letterSpacing: '-0.01em',
              }}
            >
              {t('web.home.help.title')}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 17, color: webPalette.muted, lineHeight: 1.6, maxWidth: '48ch' }}
            >
              {t('web.home.help.body')}
            </Typography>
            <Box sx={{ mt: 3.75 }}>
              <ContactTrigger>{t('web.home.help.cta')}</ContactTrigger>
            </Box>
          </Box>
        </Wrap>
      </Box>
    </SiteLayout>
  );
}

function TextLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        color: webPalette.tealInk,
        fontWeight: 600,
        fontSize: 15,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: webPalette.tealInk,
        border: `1px solid ${webPalette.line}`,
        backgroundColor: webPalette.white,
        px: 1.25,
        py: 0.5,
        borderRadius: '4px',
      }}
    >
      {children}
    </Box>
  );
}

function SmallIssue({
  slug,
  date,
  tags,
  title,
}: {
  slug: string;
  date: string;
  tags: string[];
  title: string;
}) {
  return (
    <Box
      component={RouterLink}
      to={`/bulletin/${slug}`}
      sx={{ color: 'inherit', textDecoration: 'none', display: 'block' }}
    >
      <Typography sx={{ fontSize: 13, color: webPalette.muted }}>{date}</Typography>
      <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap', my: 1.625 }}>
        {tags.map((tg) => (
          <Tag key={tg}>{tg}</Tag>
        ))}
      </Box>
      <Typography
        component="h3"
        sx={{ fontFamily: webFonts.serif, fontWeight: 500, fontSize: 21, lineHeight: 1.18 }}
      >
        {title}
      </Typography>
    </Box>
  );
}
